import React, {useState, useEffect} from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../components/SEO/SEO';
import useDecodeHtml from '../hooks/useDecodeHtml';
import useRenderBlocks from '../hooks/useRenderBlocks';
import { useSingleArticle } from '../api/fetchExampleData';
import { formatDate } from '../utils/formatDate';
import { getPrimaryCategoryName, getPrimaryCategoryUrl } from '../utils/getPrimaryCategory';
import AdSenseSidebar from '../components/ADSense/AdSenseSidebar/AdSenseSidebar';
import AdSenseArticle from '../components/ADSense/AdSenseArticle/AdSenseArticle';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5em;
  width: 100%;
  box-sizing: border-box;
  margin:0 auto;
  margin-top: 2em;
  margin-bottom: 3em;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1em; /* Riduce lo spazio tra le colonne */
    margin-top: 0em;
    margin-bottom: 1em;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem; /* Spaziatura tra gli elementi */
`;

const Sidebar = styled.aside`
  position: sticky;
  top: 20px;
  box-sizing: border-box;
  border-left: 1px solid #ddd;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CategoryLabel = styled.div`
  background-color: #cc0000;
  color: white;
  padding: 0.2rem 0.8rem;
  border-top-right-radius: 10px;
  border-bottom-left-radius: 10px;
  display: inline-block; /* Assicura che il div si adatti al contenuto */
  max-width: fit-content; /* Limita la larghezza al contenuto */
  box-sizing: border-box; /* Evita che il padding influisca sulla larghezza */
`;

const Title = styled.h1`
  font-size: 1.8rem;
  line-height: 2.2rem;
  font-weight: 800;
  color: #262626;
  margin: 0;
`;

const ResponsiveImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 5px;
  display: block;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.img`
  border-radius: 50%;
  width: 60px;
  height: 60px;
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled(Link)`
  background-color: #f1f1f1;
  border: 1px solid #454850;
  color: #cc0000;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 1rem;
  text-decoration: none;
  &:hover {
    background-color: #e0e0e0;
    text-decoration: none;
    color: #cc0000;
  }
`;

const ArticleDetail = () => {
  const { slug } = useParams();
  const decodeHtml = useDecodeHtml();
  const { data: article } = useSingleArticle(slug);
  const renderedContent = useRenderBlocks(article?.blocks || []);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {article && (
        <GridContainer>
          {/* Main Content */}
          <ContentContainer>
            <CategoryLabel>
              <Link to={getPrimaryCategoryUrl(article?.categories)} style={{ textDecoration: 'none', color: 'white' }}>
                {decodeHtml(getPrimaryCategoryName(article?.categories))}
              </Link>
            </CategoryLabel>
            <Title>{decodeHtml(article?.title)}</Title>
            <ResponsiveImage src={article?.image} alt={article?.title} />
            {article?.excerpt && <p style={{ fontStyle: 'italic', color: '#454850', margin: 0 }}>{decodeHtml(article?.excerpt)}</p>}
            {article?.caption && (
              <p style={{ margin: '0 0 15px', fontStyle: 'italic', color: '#454850' }}>
                {decodeHtml(article?.caption)}
              </p>
            )}
            <AuthorInfo>
              <Link to={`/team/${article?.author?.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <AuthorAvatar src={article?.author?.avatar} alt={article?.author?.name} />
              </Link>
              <AuthorDetails>
                <Link
                  to={`/team/${article?.author?.name.replace(/\s+/g, '-').toLowerCase()}`}
                  style={{ textDecoration: 'none', color: '#262626' }}
                >
                  <p style={{ margin: 0, fontWeight:'bold' }}>{article?.author?.name}</p>
                </Link>
                <p style={{ margin: 0, color: '#454850' }}>{formatDate(article?.date)}</p>
              </AuthorDetails>
            </AuthorInfo>
            <TagContainer>
              {article?.tags?.map((tag) => (
                <Tag to={`/tags/${tag.slug}`} key={tag.slug}>
                  {decodeHtml(tag.name)}
                </Tag>
              ))}
            </TagContainer>
            <div style={{ overflow: 'hidden', margin: 0, padding: 0 }}>{renderedContent}</div>
            {!isMobile && (
            <AdSenseArticle />
            )}
          </ContentContainer>

          {/* Sidebar */}
          {!isMobile && (
            <Sidebar>
              <AdSenseSidebar />
            </Sidebar>
          )}
        </GridContainer>
      )}
    </>
  );
};

export default ArticleDetail;
